import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import {
	DatePicker,
	Icon,
	PrimaryButton,
	SecondaryButton
} from "@prestosports/commons-ui";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";
import Add32 from "@carbon/icons-react/lib/add/32";
import React from "react";
import { useFormik } from "formik";
import Box from "@material-ui/core/Box";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Toolbar from "@material-ui/core/Toolbar";
import Chip from "@material-ui/core/Chip";
import { Input } from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import PersonnelList from "./PersonnelList";
import commonStyles from "../styles/Common.styles";
import gameDetailsStyles from "../styles/sports/GameDetails.styles";
import { useSeasonContext } from "../../../state/season/context";
import { useEventContext } from "../../../state/events/context";
import { useAppContext } from "../../../state/app/context";
import DateFormat from "../../../util/date-format";
import ResetEvent from "../../football/components/ResetEvent";
import StreamList from "./StreamList";
import { useStreamContext } from "../../../state/stream/context";
import { useAuthContext } from "../../../state/authentication/context";

const eventTypes = [
	{ id: "0", value: "Conference" },
	{ id: "1", value: "Conference Division" },
	{ id: "2", value: "National" },
	{ id: "3", value: "Neutral" },
	{ id: "4", value: "Overall" },
	{ id: "5", value: "Postseason" },
	{ id: "6", value: "Regional" },
	{ id: "7", value: "Suspended" },
	{ id: "8", value: "Weather Delay" },
	{ id: "9", value: "No Contest" },
	{ id: "10", value: "Stats Count" }
];

const EventDetails = ({ roles = [] }) => {
	const commonClasses = commonStyles();
	const classes = gameDetailsStyles();

	const { state: authState } = useAuthContext();
	const isSuperUser = authState.isSuperUser();

	const { state: seasonState } = useSeasonContext();
	const { state: eventState, saveEvent } = useEventContext();
	const { state: appState } = useAppContext();
	const { state: streamState, saveStream, deleteStream } = useStreamContext();

	const streams = streamState.streams();

	const currentSeason = seasonState.currentSeason();
	const currentEvent = eventState.currentEvent();

	const timeZones = appState.timeZones();
	const getEventTypes = (event) => {
		const selectedTypes = [];
		if (event.eventType.isConference) selectedTypes.push(eventTypes[0]);
		if (event.eventType.isDivision) selectedTypes.push(eventTypes[1]);
		if (event.eventType.isNational) selectedTypes.push(eventTypes[2]);
		if (event.eventType.isNeutral) selectedTypes.push(eventTypes[3]);
		if (event.eventType.isOverall) selectedTypes.push(eventTypes[4]);
		if (event.eventType.isPostSeason) selectedTypes.push(eventTypes[5]);
		if (event.eventType.isRegional) selectedTypes.push(eventTypes[6]);
		if (event.eventType.isSuspended) selectedTypes.push(eventTypes[7]);
		if (event.eventType.isWeatherDelay) selectedTypes.push(eventTypes[8]);
		if (event.eventType.noContest) selectedTypes.push(eventTypes[9]);
		if (event.eventType.statsCount) selectedTypes.push(eventTypes[10]);
		return selectedTypes;
	};

	const selectedEventTypes = getEventTypes(currentEvent);

	const disableTimeAndType = true;
	const fullEventDate = currentEvent
		? `${DateFormat.formatIsoUtcWithTz(
				currentEvent.startDateTime,
				currentEvent.timeZone,
				"MM/DD/yy"
		  )} ${DateFormat.formatIsoUtcWithTz(
				currentEvent.startDateTime,
				currentEvent.timeZone,
				"hh:mm a"
		  ).toUpperCase()} ${
				timeZones.find((tz) => tz.value === currentEvent.timeZone)?.name
		  }`
		: "";

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			eventId: "",
			venue: "",
			weather: "",
			temperature: "",
			address: "",
			city: "",
			stateCountry: "",
			timeZone: "",
			startDate:
				(currentEvent &&
					DateFormat.formatIsoUtcWithTz(
						currentEvent.startDateTime,
						currentEvent.timeZone,
						"MM/DD/yy"
					)) ||
				"",
			startTime:
				(currentEvent &&
					DateFormat.formatIsoUtcWithTz(
						currentEvent.startDateTime,
						currentEvent.timeZone,
						"HH:mm"
					)) ||
				"",
			personnel: [],
			eventType: {
				isNeutral: false
			},
			...currentEvent
		},
		onSubmit: (values) => {
			const startDateTime = DateFormat.toISOStringWithTz(
				values.startDate,
				values.startTime,
				values.timeZone
			);

			saveEvent(
				Object.assign(currentEvent, values, {
					startDateTime
				})
			);
		}
	});

	const { handleSubmit, handleChange, setFieldValue, setValues, resetForm } =
		formik;

	const addPersonnel = () => {
		const id = Math.max(0, ...formik.values.personnel.map((p) => p.id)) + 1;
		setFieldValue("personnel", [
			...formik.values.personnel,
			{ id, fullName: "", role: "" }
		]);
	};

	const removePersonnel = (personnel) => {
		setFieldValue(
			"personnel",
			formik.values.personnel.filter((p) => p.id !== personnel.id)
		);
	};

	const editPersonnel = (personnel) => {
		setFieldValue(
			"personnel",
			formik.values.personnel.map((p) => {
				if (p.id !== personnel.id) {
					return p;
				}
				return Object.assign(p, personnel);
			})
		);
	};

	const onEventTypeSelect = (event, typesList) => {
		const newValues = {
			...formik.values,
			eventType: {
				...formik.values.eventType,
				isConference: !!typesList.find((type) => type.id === "0"),
				isRegional: !!typesList.find((type) => type.id === "1"),
				isDivision: !!typesList.find((type) => type.id === "2"),
				isPostSeason: !!typesList.find((type) => type.id === "3"),
				isOverall: !typesList.find((type) => type.id === "4")
			}
		};
		setValues(newValues);
	};

	// const onNeutralSiteClick = (event, isNeutralSite) => {
	// 	const newValues = {
	// 		...formik.values,
	// 		eventType: {
	// 			...formik.values.eventType,
	// 			isNeutral: isNeutralSite
	// 		}
	// 	};
	// 	setValues(newValues);
	// };

	return (
		currentEvent &&
		currentSeason && (
			<Box className={commonClasses.container}>
				<Box className={classes.contentContainer}>
					<form className={commonClasses.form} onSubmit={handleSubmit}>
						<Grid container spacing={5} className={classes.detailsContainer}>
							<Grid item md={6} className={classes.firstColumn}>
								<Grid container spacing={3}>
									{disableTimeAndType && (
										<>
											<Grid item xs={12} md={12}>
												<TextField
													disabled
													label="Event Date"
													fullWidth
													value={fullEventDate}
												/>
											</Grid>
											{!!selectedEventTypes.length && (
												<Grid className={classes.eventTypeContainer} item xs={12} md={12}>
													<FormControl variant="outlined" disabled fullWidth>
														<InputLabel htmlFor="event-type">Status / Description</InputLabel>
														<Input
															disableUnderline
															id="event-type"
															startAdornment={
																<InputAdornment position="start">
																	{selectedEventTypes.map((et) => (
																		<Chip color="secondary" label={et.value} />
																	))}
																</InputAdornment>
															}
														/>
													</FormControl>
												</Grid>
											)}
										</>
									)}
									{!disableTimeAndType && (
										<>
											<Grid className={classes.startDate} item xs={12} md={6}>
												<DatePicker
													value={formik.values.startDate}
													onChange={(value) => {
														setFieldValue("startDate", value);
													}}
													placeholder="Start Date"
													format="MM/DD/yy"
													startDate={currentSeason.startDate}
													endDate={currentSeason.endDate}
													multiple={false}
													editable={false}
												/>
											</Grid>
											<Grid item xs={12} md={6}>
												<TextField
													id="startTime"
													name="startTime"
													label="Time"
													fullWidth
													type="time"
													value={formik.values.startTime}
													onChange={handleChange}
													InputLabelProps={{ shrink: true }}
												/>
											</Grid>
											<Grid item xs={12}>
												<FormControl variant="outlined" fullWidth>
													<InputLabel htmlFor="timeZone">Time Zone</InputLabel>
													<Select
														id="timeZone"
														name="timeZone"
														label="Time Zone"
														value={formik.values.timeZone}
														required
														onChange={handleChange}
													>
														<MenuItem value="" key="none">
															None
														</MenuItem>
														{timeZones.map((r) => (
															<MenuItem value={r.value} key={r.value}>
																{r.name}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</Grid>
											<Grid item xs={12}>
												<Autocomplete
													onChange={onEventTypeSelect}
													defaultValue={getEventTypes(currentEvent)}
													limitTags={1}
													multiple
													id="eventTypeDesc"
													options={eventTypes}
													disableCloseOnSelect
													getOptionLabel={(option) => option.value}
													renderOption={(option, { selected }) => (
														<>
															<Checkbox color="primary" size="small" checked={selected} />
															{option.value}
														</>
													)}
													renderInput={(params) => (
														<TextField {...params} label="Event Type" />
													)}
													placeholder="Event Type"
												/>
											</Grid>
										</>
									)}
									<Grid item xs={12} sm={8}>
										<TextField
											id="weather"
											name="weather"
											label="Weather"
											fullWidth
											value={formik.values.weather}
											onChange={handleChange}
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<TextField
											id="temperature"
											name="temperature"
											label="Temp"
											fullWidth
											type="number"
											value={formik.values.temperature}
											onChange={handleChange}
										/>
									</Grid>
									<Grid item xs={12} sm={8}>
										<TextField
											disabled
											id="venue"
											name="venue"
											label="Venue"
											fullWidth
											value={formik.values.venue}
											onChange={handleChange}
										/>
									</Grid>
									{/* <Grid item xs={12} sm={4}> */}
									{/* 	<FormControlLabel */}
									{/* 		control={ */}
									{/* 			<Checkbox */}
									{/* 				id="neutralSite" */}
									{/* 				name="neutralSite" */}
									{/* 				checked={formik.values.eventType.isNeutral} */}
									{/* 				onChange={onNeutralSiteClick} */}
									{/* 			/> */}
									{/* 		} */}
									{/* 		label="Neutral Site" */}
									{/* 	/> */}
									{/* </Grid> */}
									<Grid item xs={12}>
										<TextField
											disabled
											id="address"
											name="address"
											label="Address"
											fullWidth
											value={formik.values.address}
											onChange={handleChange}
										/>
									</Grid>
									<Grid item xs={12}>
										<TextField
											disabled
											id="city"
											name="city"
											label="City"
											fullWidth
											value={formik.values.city}
											onChange={handleChange}
										/>
									</Grid>
									<Grid item xs={12}>
										<TextField
											disabled
											id="stateCountry"
											name="stateCountry"
											label="State/Country"
											fullWidth
											value={formik.values.stateCountry}
											onChange={handleChange}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item md={6}>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<TextField
											disabled
											id="eventId"
											name="eventId"
											label="Event ID"
											fullWidth
											value={formik.values.eventId}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Typography variant="h5">Personnel</Typography>
									</Grid>
									<Grid item xs={12} sm={6} style={{ textAlign: "right" }}>
										<SecondaryButton
											id="button-add-personnel"
											startIcon={
												<Icon>
													<Add32 />
												</Icon>
											}
											onClick={addPersonnel}
											label="Add Personnel"
										/>
									</Grid>
									<Grid item xs={12}>
										<PersonnelList
											personnel={formik.values.personnel}
											roles={roles}
											onChange={editPersonnel}
											onDelete={removePersonnel}
										/>
									</Grid>
									{isSuperUser && (
										<StreamList
											eventId={currentEvent.id}
											streams={streams}
											onSave={saveStream}
											onDelete={deleteStream}
										/>
									)}
								</Grid>
							</Grid>
						</Grid>
						<Toolbar className={commonClasses.actionToolbar}>
							<Grid container direction="row" justifyContent="space-between">
								<Grid item>
									<ResetEvent />
								</Grid>

								<Grid item>
									<Grid container direction="row" justifyContent="flex-end" spacing={1}>
										<Grid item>
											<SecondaryButton label="Cancel" onClick={resetForm} />
										</Grid>

										<Grid item>
											<PrimaryButton id="button-save" type="submit" label="Save" />
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Toolbar>
					</form>
				</Box>
			</Box>
		)
	);
};

export default EventDetails;
